/** Features with life in them: firefly loops, butterfly pockets, the amoeba, the magic wall. */

import type { Rng } from '../../sim/rng';
import type { Plan } from './plan';
import { awayFromInbox, between, chance } from './plan';
import type { Cell, Rect, Sketch } from './sketch';
import { rectDistance } from './sketch';

/** A random cell of a rect's outline, corners included only if `corners`. */
function edgeCell(rng: Rng, r: Rect, corners: boolean): Cell {
  const inset = corners ? 0 : 1;
  const side = rng.int(4);
  if (side < 2) return { x: r.x + inset + rng.int(r.w - 2 * inset), y: side === 0 ? r.y : r.y + r.h - 1 };
  return { x: side === 2 ? r.x : r.x + r.w - 1, y: r.y + inset + rng.int(r.h - 2 * inset) };
}

/**
 * A brick ring around a one-cell space track with a firefly on it, and a core of dirt and
 * diamonds inside. Dirt plugs in the ring let Digby dig in; the firefly cannot leave through
 * dirt, so it circles until someone opens the way.
 */
export function fireflyLoop(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const ow = plan.bonus ? between(rng, 6, 8) : between(rng, 7, 12);
  const oh = plan.bonus ? 6 : between(rng, 6, 8);
  const spot = sk.findSpot(rng, ow + 2, oh + 2, awayFromInbox(plan));
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const ring = { x: spot.x + 1, y: spot.y + 1, w: ow, h: oh };
  const track = { x: ring.x + 1, y: ring.y + 1, w: ow - 2, h: oh - 2 };
  sk.outline(ring, 'w');
  sk.outline(track, ' ');
  // Core diamonds must not move: none on the bottom core row (the track is under it), and on
  // the edge columns only over dirt, since a gem on a gem would roll off into the track.
  const x0 = track.x + 1;
  const x1 = track.x + track.w - 2;
  for (let y = track.y + track.h - 3; y > track.y; y--) {
    for (let x = x0; x <= x1; x++) {
      const edge = x === x0 || x === x1;
      if (chance(rng, 55) && (!edge || sk.get(x, y + 1) === '.')) sk.set(x, y, 'd');
    }
  }
  const plugs = 1 + rng.int(2);
  for (let i = 0; i < plugs; i++) {
    const c = edgeCell(rng, ring, false);
    sk.set(c.x, c.y, '.');
  }
  const flies = ow >= 10 && plan.depth >= 8 ? 2 : 1;
  for (let i = 0; i < flies; i++) {
    const c = edgeCell(rng, track, true);
    if (sk.get(c.x, c.y) !== ' ') continue;
    sk.set(c.x, c.y, rng.oneIn(2) ? 'f' : 'F');
    plan.creatures.push(c);
  }
  return true;
}

/**
 * A butterfly in a small space pocket under a dirt roof, with boulders sitting on the roof:
 * open the roof under one and it drops on the butterfly, which bursts into nine diamonds.
 */
export function butterflyPocket(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const pw = between(rng, 3, 4);
  const ph = 2;
  const spot = sk.findSpot(rng, pw + 2, ph + 4, awayFromInbox(plan));
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const x0 = spot.x + 1;
  for (let i = 0; i < pw; i++) sk.set(x0 + i, spot.y + 1, 'r');
  const pocket = { x: x0, y: spot.y + 3, w: pw, h: ph };
  sk.fillRect(pocket, ' ');
  const c = { x: x0 + rng.int(pw), y: pocket.y + rng.int(ph) };
  sk.set(c.x, c.y, rng.oneIn(2) ? 'b' : 'B');
  plan.creatures.push(c);
  return true;
}

/**
 * An amoeba nursery: a seed in a brick box whose one way out is a dirt gap in the roof, with a
 * boulder resting on the roof beside it. Dig out the gap and the cell above it and the boulder
 * rolls in and seals the box: the amoeba fills it, finds itself enclosed and turns to diamonds.
 * Leave it and the amoeba eats the gap and spills out. Kept well away from the inbox, the exit
 * (it would wall it in) and every creature (it sets them off on touch).
 */
export function amoebaNursery(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const ow = between(rng, 6, 8);
  const oh = between(rng, 5, 6);
  const far = (r: Rect): boolean =>
    rectDistance(r, plan.inbox) >= 9 &&
    (plan.exit === undefined || rectDistance(r, plan.exit) >= 6) &&
    plan.creatures.every((c) => rectDistance(r, c) >= 8);
  const spot = sk.findSpot(rng, ow + 2, oh + 2, far);
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const box = { x: spot.x + 1, y: spot.y + 1, w: ow, h: oh };
  sk.outline(box, 'w');
  const gap = box.x + 2 + rng.int(ow - 4);
  sk.set(gap, box.y, '.');
  sk.set(rng.oneIn(2) ? gap - 1 : gap + 1, box.y - 1, 'r');
  // The seed starts at the bottom, as far from the gap as the box allows.
  const seed = { x: box.x + 1 + rng.int(ow - 2), y: box.y + oh - 2 };
  sk.set(seed.x, seed.y, 'a');
  plan.amoebaTime = Math.max(25, 80 - 3 * plan.depth) + rng.int(10);
  return true;
}

/**
 * A magic wall with boulders resting on dirt above it and an empty basin below. Dig out the dirt
 * between and the boulders drop through, coming out as diamonds while the wall runs.
 */
export function magicWall(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const len = plan.bonus ? between(rng, 4, 6) : between(rng, 4, 8);
  const spot = sk.findSpot(rng, len + 2, 7);
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  let boulders = 0;
  for (let i = 0; i < len; i++) {
    const x = spot.x + 1 + i;
    if (chance(rng, 75) || (i === len - 1 && boulders < 3)) {
      sk.set(x, spot.y + 1, 'r');
      boulders++;
    }
    sk.set(x, spot.y + 3, 'm');
    sk.set(x, spot.y + 4, ' ');
    sk.set(x, spot.y + 5, ' ');
  }
  plan.magicWallTime = plan.bonus ? between(rng, 8, 15) : between(rng, 15, 30);
  return true;
}
